import { useMemo } from 'react'
import { infiniteQueryOptions, useInfiniteQuery } from '@tanstack/react-query'
import {
  getEngines,
  postDiscoverSearch,
  type GetEnginesSort,
} from '@/types/origin-point-engine'
import { useShallow } from 'zustand/react/shallow'
import { logger } from '@/lib/logger'
import { useDebounce } from '@/hooks/use-debounce'
import { useDiscoverStore } from '../stores/discover-store'
import type { EnginesResponse, SearchEnginesResponse } from '../types/type'

export interface DiscoverQueryParams {
  searchQuery: string
  category?: string
  sort?: string
  order?: 'asc' | 'desc'
  tags: string[]
  limit: number
}

export const discoverQueryOptions = (params: DiscoverQueryParams) => {
  return infiniteQueryOptions({
    queryKey: ['discover', params],
    queryFn: async ({ pageParam = 1 }) => {
      const {
        searchQuery: q,
        category: c,
        sort: s,
        order: o,
        tags: t,
        limit: l,
      } = params
      logger.debug(t)
      const tags = t.length > 0 ? t : undefined
      if (q) {
        // POST search
        const res = await postDiscoverSearch({
          query: q,
          category: c,
          limit: l,
          page: pageParam,
          order: o,
          sortBy: s,
          // 后端已经改为 string[]，无需在意 orval 生成的类型
          tags: tags,
        })

        // Data Normalization
        const searchData = (res as unknown as SearchEnginesResponse).data
        const engines = searchData.engines ?? { items: [], total: 0 } // 确保 engines 为 null 时有默认值
        return {
          list: engines.items ?? [],
          page: searchData.page,
          totalPages: Math.ceil(engines.total / l),
        }
      } else {
        // GET defaults
        const res = await getEngines({
          page: pageParam,
          limit: l,
          category: c,
          //视为GetEnginesSort类型
          sort: s as GetEnginesSort,
          order: o,
          tags: JSON.stringify(tags), //后端已经改为string[],无需在意orval类型
        })

        // Data Normalization
        const engineData = (res as unknown as EnginesResponse).data
        return {
          list: engineData?.engines ?? [], // 确保 engines 为 null 时返回空数组
          page: engineData.pagination.page,
          totalPages: engineData.pagination.pages,
        }
      }
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      if (lastPage.page < lastPage.totalPages) {
        return lastPage.page + 1
      }
      return undefined
    },
  })
}

export const useDiscover = () => {
  const { limit, searchQuery, category, sort, order, tags } = useDiscoverStore(
    useShallow((s) => ({
      limit: s.searchParams.limit,
      searchQuery: s.searchParams.searchQuery,
      category: s.searchParams.category,
      sort: s.searchParams.sort,
      order: s.searchParams.order,
      tags: s.searchParams.tags,
    }))
  )

  // Memoize the parameters object to provide a stable reference to useDebounce
  // Prevents the debounce timer from being reset on every irrelevant re-render
  const searchParamsValue = useMemo(
    () => ({
      searchQuery,
      category,
      sort,
      order,
      tags,
      limit,
    }),
    [searchQuery, category, sort, order, tags, limit]
  )

  const debouncedParams = useDebounce(
    searchParamsValue,
    1000 //防抖1s
  )

  const query = useInfiniteQuery(discoverQueryOptions(debouncedParams))

  // Flatten data for UI
  const flatEngines = useMemo(() => {
    return query.data?.pages.flatMap((page) => page.list) ?? []
  }, [query.data])

  return {
    ...query,
    flatEngines,
  }
}
